import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import {
  FiGrid,
  FiTrendingUp,
  FiCpu,
  FiDollarSign,
  FiLogOut,
  FiShield,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiMenu,
  FiX,
} from 'react-icons/fi';
import Setup2FAModal from './Setup2FAModal';

const navItems = [
  { to: '/dashboard',            label: 'Visão Geral',        icon: FiGrid,      end: true },
  { to: '/dashboard/projecao',   label: 'Projeção Mensal',    icon: FiTrendingUp           },
  { to: '/dashboard/assistente', label: 'Copiloto Financeiro', icon: FiCpu                  },
  { to: '/dashboard/caixa',      label: 'Controle de Caixa',  icon: FiDollarSign           },
];

export default function SidebarLayout({ children }) {
  const navigate = useNavigate();
  const { usuario, logout } = useAuthStore();
  const [isSetup2FAOpen, setIsSetup2FAOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);        // mobile drawer
  const [isCollapsed, setIsCollapsed] = useState(false);        // desktop collapse

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('Logout realizado');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">

      {/* ── MOBILE HEADER ── */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between md:hidden">
        <div className="flex items-center gap-3">
          <img src="/logo-cacintex.jpeg" alt="Cacintex" className="h-8 w-8 rounded-lg object-cover" />
          <span className="text-lg font-semibold text-slate-900">Cacintex</span>
        </div>
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
          <FiMenu size={22} />
        </button>
      </header>

      {/* ── OVERLAY (mobile only) ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50 flex-shrink-0 h-full
          bg-white border-r border-gray-100 flex flex-col shadow-sm
          transform transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
          ${isCollapsed ? 'md:w-[72px]' : 'md:w-64'} w-64
        `}
      >

        {/* Logo / Brand */}
        <div className={`pt-8 pb-6 border-b border-gray-100 transition-all duration-300 ${isCollapsed ? 'px-3' : 'px-6'}`}>
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'gap-3'}`}>
              <img
                src="/logo-cacintex.jpeg"
                alt="Cacintex"
                className="h-9 w-9 rounded-lg object-cover flex-shrink-0 shadow-sm"
              />
              <div className={`overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                <h1 className="text-xl font-black text-gray-900 tracking-tight leading-tight whitespace-nowrap">Cacintex</h1>
                <p className="text-xs text-gray-400 font-medium whitespace-nowrap">Gestão de Importações</p>
              </div>
            </div>
            {/* Mobile close button */}
            <button onClick={() => setSidebarOpen(false)} className={`p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md md:hidden transition-colors ${isCollapsed ? 'hidden' : ''}`}>
              <FiX size={20} />
            </button>
          </div>

          {/* Desktop collapse toggle */}
          <button
            onClick={() => setIsCollapsed(prev => !prev)}
            className="hidden md:flex items-center justify-center w-full mt-4 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-all duration-150"
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {isCollapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
            <span className={`ml-2 text-xs font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
              Recolher
            </span>
          </button>
        </div>

        {/* User info */}
        <div className={`py-4 border-b border-gray-100 transition-all duration-300 ${isCollapsed ? 'px-3' : 'px-6'}`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" title={isCollapsed ? (usuario?.nome || 'Usuário') : undefined}>
              <span className="text-indigo-700 font-semibold text-sm">
                {(usuario?.nome || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className={`min-w-0 overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
              <p className="text-sm font-bold text-gray-800 truncate whitespace-nowrap">{usuario?.nome || 'Usuário'}</p>
              <p className="text-xs text-gray-400 truncate whitespace-nowrap">{usuario?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <p className={`px-3 text-[10px] uppercase tracking-widest font-black text-gray-400 mb-2 whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'opacity-0 h-0 mb-0' : 'opacity-100 h-auto'}`}>
            Navegação
          </p>
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setSidebarOpen(false)}
              title={isCollapsed ? label : undefined}
              className={({ isActive }) =>
                `group relative flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={isCollapsed ? 18 : 15}
                    className={`flex-shrink-0 transition-all duration-150 ${
                      isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'
                    }`}
                  />
                  <span className={`flex-1 whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100'}`}>{label}</span>
                  {isActive && !isCollapsed && (
                    <FiChevronRight size={13} className="text-indigo-400 flex-shrink-0" />
                  )}
                  {/* Collapsed tooltip */}
                  {isCollapsed && (
                    <span className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-[60]">
                      {label}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer actions */}
        <div className="px-3 pb-6 pt-2 border-t border-gray-100 space-y-1 mt-2">
          <button
            onClick={() => setIsSetup2FAOpen(true)}
            title={isCollapsed ? 'Configurar 2FA' : undefined}
            className={`group relative w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all duration-150`}
          >
            <FiShield size={isCollapsed ? 18 : 15} className="text-slate-400 group-hover:text-slate-600 flex-shrink-0" />
            <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100'}`}>Configurar 2FA</span>
            {isCollapsed && (
              <span className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-[60]">
                Configurar 2FA
              </span>
            )}
          </button>
          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Sair' : undefined}
            className={`group relative w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md text-sm font-medium text-red-500 hover:bg-red-50 hover:text-red-700 transition-all duration-150`}
          >
            <FiLogOut size={isCollapsed ? 18 : 15} className="flex-shrink-0" />
            <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100'}`}>Sair</span>
            {isCollapsed && (
              <span className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-[60]">
                Sair
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 h-full overflow-y-auto min-w-0 pt-14 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 py-6 md:px-6 md:py-8">
          {children}
        </div>
      </main>

      {/* Global 2FA Modal */}
      <Setup2FAModal
        isOpen={isSetup2FAOpen}
        onClose={() => setIsSetup2FAOpen(false)}
      />
    </div>
  );
}
